import React, { useState } from "react";
import { motion } from "motion/react";
import { usePrefersReducedMotion } from "../../../../hooks/usePrefersReducedMotion";

const cardTextClass = () => "text-black transition-all duration-200";

const cardTitleClass = (compact) =>
  (compact ? "text-sm font-medium " : "mb-5 ") +
  "border-b border-[#9ca3af] pb-2 " +
  cardTextClass();

const cardValueClass = (isHovered, compactCard) =>
  (compactCard ? "text-3xl md:text-4xl " : "text-5xl ") +
  "leading-[0.95] transition-all duration-200 " +
  (isHovered ? "font-bold text-black" : "font-semibold text-black");

const cardDetailsClass = (isHovered) =>
  "min-h-[48px] max-h-[48px] overflow-hidden transition-all duration-200 font-base " +
  (isHovered ? "text-black" : "text-transparent");

const idleShadow = "0px 0px 0px rgba(0, 0, 0, 0)";

/** Stable kebab-case slug from a card key/title for data-testid attributes. */
export function toStatsCardTestIdSlug(value) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Reusable dashboard-style stat cards with optional hover details.
 */
export function StatsCards({
  cards = [],
  className = "",
  showDetails = true,
  justify = "left",
  testIdPrefix = "dashboard",
}) {
  const [hover, setHover] = useState();
  const reduced = usePrefersReducedMotion();

  const justifyClass = justify === "center" ? "justify-center" : "justify-start";

  const cardBaseClass = showDetails
    ? "m-[5px] flex h-[200px] w-[23%] cursor-pointer flex-col rounded-[18px] bg-white px-[34px] py-[20px] transition-colors duration-200 max-[750px]:w-full"
    : "m-[5px] flex h-[120px] w-[18%] cursor-pointer flex-col rounded-[14px] bg-white px-5 py-3 transition-colors duration-200 max-[750px]:w-full";

  const cardTransition = reduced
    ? { duration: 0 }
    : { type: "spring", stiffness: 100, damping: 18 };

  const valueTransition = (delay) =>
    reduced
      ? { duration: 0 }
      : { delay, type: "spring", stiffness: 200, damping: 20 };

  return (
    <div
      className={`flex flex-wrap ${justifyClass} ${className}`.trim()}
      data-testid={`${testIdPrefix}-stats`}
    >
      {cards.map((card, index) => {
        const isHovered = hover === card.key;
        const detailsText = card.details ?? "";
        const delay = reduced ? 0 : index * 0.1;
        const valueDelay = reduced ? 0 : delay + 0.3;
        const cardSlug = toStatsCardTestIdSlug(card.key || card.title || index);
        const cardTestId = `${testIdPrefix}-card-${cardSlug}`;

        return (
          <motion.div
            key={card.key}
            role={card.onClick ? "button" : undefined}
            tabIndex={card.onClick ? 0 : undefined}
            className={cardBaseClass}
            data-testid={cardTestId}
            initial={
              reduced
                ? false
                : {
                    opacity: 0,
                    y: 30,
                    boxShadow: idleShadow,
                  }
            }
            animate={{
              opacity: 1,
              y: 0,
              boxShadow: idleShadow,
            }}
            transition={{ ...cardTransition, delay }}
            whileHover={
              reduced
                ? {}
                : {
                    scale: 1.05,
                    y: -5,
                    boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.1)",
                  }
            }
            whileTap={reduced ? {} : { scale: 0.98 }}
            onMouseEnter={() => setHover(card.key)}
            onMouseLeave={() => setHover()}
            onClick={card.onClick}
            onKeyDown={(e) => {
              if (card.onClick && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                card.onClick();
              }
            }}
          >
            <div className="flex h-full flex-col">
              <div
                className={cardTitleClass(!showDetails)}
                data-testid={`${cardTestId}-title`}
              >
                {card.title}
              </div>
              {showDetails ? (
                <>
                  <div className="flex justify-between pb-2">
                    <motion.span
                      className={cardValueClass(isHovered, false)}
                      data-testid={`${cardTestId}-value`}
                      initial={reduced ? false : { scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={valueTransition(valueDelay)}
                    >
                      {card.value}
                    </motion.span>
                  </div>
                  <div
                    className={cardDetailsClass(isHovered)}
                    data-testid={`${cardTestId}-details`}
                  >
                    {detailsText}
                  </div>
                </>
              ) : (
                <div className="flex flex-1 items-center pb-1">
                  <motion.span
                    className={cardValueClass(isHovered, true)}
                    data-testid={`${cardTestId}-value`}
                    initial={reduced ? false : { scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={valueTransition(valueDelay)}
                  >
                    {card.value}
                  </motion.span>
                </div>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

export default StatsCards;
